import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { Notification } from '../../services/dashboardService';

// Types
interface UIState {
  activeTab: number;
  modals: {
    addInstructor: boolean;
    createVenue: boolean;
    seoEdit: boolean;
    createAssignment: boolean;
  };
  venueModalTab: number;
  uploading: boolean;
  notifications: Notification[];
  successMessage: string | null;
  errorMessage: string | null;
}

// Initial state
const initialState: UIState = {
  activeTab: 0,
  modals: {
    addInstructor: false,
    createVenue: false,
    seoEdit: false,
    createAssignment: false,
  },
  venueModalTab: 0,
  uploading: false,
  notifications: [],
  successMessage: null,
  errorMessage: null,
};

// Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<number>) => {
      state.activeTab = action.payload;
    },
    openModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      state.modals[action.payload] = false;
    },
    setVenueModalTab: (state, action: PayloadAction<number>) => {
      state.venueModalTab = action.payload;
    },
    setUploading: (state, action: PayloadAction<boolean>) => {
      state.uploading = action.payload;
    },
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.notifications = action.payload;
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload);
    },
    removeNotification: (state, action: PayloadAction<number>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
      if (action.payload) {
        state.errorMessage = null;
      }
    },
    setErrorMessage: (state, action: PayloadAction<string | null>) => {
      state.errorMessage = action.payload;
      if (action.payload) {
        state.successMessage = null;
      }
    },
    clearMessages: (state) => {
      state.successMessage = null;
      state.errorMessage = null;
    },
    resetUIState: () => initialState,
  },
});

// Export actions
export const {
  setActiveTab,
  openModal,
  closeModal,
  setVenueModalTab,
  setUploading,
  setNotifications,
  addNotification,
  removeNotification,
  setSuccessMessage,
  setErrorMessage,
  clearMessages,
  resetUIState,
} = uiSlice.actions;

// Export selectors
export const selectActiveTab = (state: { ui: UIState }) => state.ui.activeTab;
export const selectModals = (state: { ui: UIState }) => state.ui.modals;
export const selectVenueModalTab = (state: { ui: UIState }) => state.ui.venueModalTab;
export const selectUploading = (state: { ui: UIState }) => state.ui.uploading;
export const selectNotifications = (state: { ui: UIState }) => state.ui.notifications;
export const selectMessages = createSelector(
  [(state: { ui: UIState }) => state.ui],
  (ui) => ({
    successMessage: ui.successMessage,
    errorMessage: ui.errorMessage,
  })
);

// Export reducer
export default uiSlice.reducer;

